import { useQuery } from '@tanstack/react-query';

import {
  AppliedPolicyStatus,
  EnvironmentId,
} from '@/react/portainer/environments/types';
import { ObservabilityK8sFormValues } from '@/react/portainer/policies/components/kubernetes/observability/types';
import { getPods } from '@/react/kubernetes/applications/queries/getPods';
import { withError } from '@/react-tools/react-query';

import { FormSection } from '@@/form-components/FormSection';

interface Props {
  formData: Partial<ObservabilityK8sFormValues>;
  endpointId?: EnvironmentId;
  policyStatus?: AppliedPolicyStatus;
}

function SettingsList({ children }: { children: React.ReactNode }) {
  return (
    <div className="space-y-1 overflow-hidden rounded-lg border border-gray-5 th-dark:border-gray-7">
      {children}
    </div>
  );
}

function SettingRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-center justify-between bg-gray-2 px-4 py-3 th-dark:bg-gray-iron-10">
      <span className="text-sm">{label}</span>
      <span className="text-sm font-medium">{value}</span>
    </div>
  );
}

export function ObservabilityK8sPolicyReadOnly({
  formData,
  endpointId,
  policyStatus,
}: Props) {
  const isDeployMode = formData.mode === 'deploy-and-connect';
  const modeLabel = isDeployMode
    ? 'Deploy agents and connect'
    : 'Connect to existing agents';

  const connectOnlyNamespace = formData.detection?.expectedNamespace ?? '';
  const connectOnlyPodsEnabled =
    !isDeployMode && !!endpointId && !!connectOnlyNamespace;
  const connectOnlyPodsQuery = useConnectOnlyPods(
    endpointId,
    connectOnlyNamespace,
    connectOnlyPodsEnabled
  );
  const connectOnlyPods = connectOnlyPodsQuery.data ?? [];

  return (
    <>
      <FormSection title="OneUptime Connection">
        <SettingsList>
          <SettingRow
            label="OneUptime URL"
            value={formData.oneUptimeURL || '—'}
          />
          <SettingRow label="Project ID" value={formData.projectID || '—'} />
        </SettingsList>
      </FormSection>

      <FormSection title="Agent Configuration">
        <SettingsList>
          <SettingRow label="Mode" value={modeLabel} />
          {isDeployMode ? (
            <>
              {!!formData.deploy?.agentURL && (
                <SettingRow
                  label="Agent-facing OneUptime URL"
                  value={formData.deploy.agentURL}
                />
              )}
              <SettingRow
                label="Target namespace"
                value={formData.deploy?.targetNamespace || '—'}
              />
              <SettingRow
                label="Helm release name"
                value={formData.deploy?.helmReleaseName || '—'}
              />
            </>
          ) : (
            <>
              <SettingRow
                label="Expected namespace"
                value={formData.detection?.expectedNamespace || '—'}
              />
              <SettingRow
                label="Expected Helm release name"
                value={formData.detection?.helmReleaseName || '—'}
              />
            </>
          )}
        </SettingsList>
      </FormSection>

      {policyStatus && (
        <FormSection title="Agent Status">
          <SettingsList>
            <SettingRow label="Status" value={policyStatus.Status} />
            {policyStatus.Message && (
              <SettingRow label="Details" value={policyStatus.Message} />
            )}
            {!!policyStatus.LastAttemptTime && (
              <SettingRow
                label="Last checked"
                value={new Date(
                  policyStatus.LastAttemptTime * 1000
                ).toLocaleString()}
              />
            )}
          </SettingsList>
          {connectOnlyPods.length > 0 && (
            <div className="mt-2 space-y-1 overflow-hidden rounded-lg border border-gray-5 th-dark:border-gray-7">
              {connectOnlyPods.map((pod) => (
                <div
                  key={pod.metadata?.name}
                  className="flex items-center justify-between bg-gray-2 px-4 py-3 th-dark:bg-gray-iron-10"
                >
                  <span className="text-sm">{pod.metadata?.name}</span>
                  <span className="text-sm font-medium">
                    {pod.status?.phase || '—'}
                  </span>
                </div>
              ))}
            </div>
          )}
        </FormSection>
      )}
    </>
  );
}

function useConnectOnlyPods(
  endpointId: EnvironmentId | undefined,
  namespace: string,
  enabled: boolean
) {
  return useQuery(
    ['observability-connect-only-pods', endpointId, namespace],
    () => getPods(endpointId!, namespace),
    { enabled, ...withError('Unable to load agent pods') }
  );
}
